// LeadCallIntelligence — Phase 4.
//
// Per-call analytics for this lead. Reads whatever the call rows
// already carry — duration, aiScore, outcome, notes — from the
// callHistory prop the drawer already receives. Aggregates:
//
//   Avg AI score across calls
//   Total talk time
//   Longest call
//   Best-outcome flag
//   Coaching hints from patterns
//
// Nothing fabricated. If a field isn't present on the row, we
// simply don't render it — no fake analytics.

import React from 'react';

function fmtDuration(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${Math.round(s)}s`;
}

function outcomeStyle(outcome) {
  const o = String(outcome || '').toLowerCase();
  if (o.includes('sold') || o.includes('appt') || o.includes('interested'))
    return { bg: '#dcfce7', text: '#166534', border: '#86efac' };
  if (o.includes('voicemail') || o.includes('no answer'))
    return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
  if (o.includes('not interested') || o.includes('do not call'))
    return { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' };
  return { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' };
}

export default function LeadCallIntelligence({ callHistory = [] }) {
  if (callHistory.length === 0) return null;

  const totalDuration = callHistory.reduce((s, c) => s + Number(c.duration || c.duration_sec || 0), 0);
  const scores        = callHistory.map(c => Number(c.aiScore || c.ai_score || 0)).filter(n => n > 0);
  const avgScore      = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
  const longestCall   = callHistory.reduce((max, c) => {
    const d = Number(c.duration || c.duration_sec || 0);
    return d > (max?.duration || 0) ? { call: c, duration: d } : max;
  }, null);

  const outcomes = callHistory.map(c => c.outcome).filter(Boolean);
  const outcomeCounts = new Map();
  for (const o of outcomes) outcomeCounts.set(o, (outcomeCounts.get(o) || 0) + 1);
  const topOutcome = [...outcomeCounts.entries()].sort((a, b) => b[1] - a[1])[0];

  const recentCalls = callHistory.slice(0, 3);

  // Coaching signal: high volume + low score = they answer but the
  // conversation isn't landing.
  const coachingSignals = [];
  if (avgScore != null && avgScore < 50 && callHistory.length >= 3) {
    coachingSignals.push('Multiple calls but low avg score — try a different opener next time');
  }
  if (totalDuration > 0 && totalDuration / callHistory.length < 60) {
    coachingSignals.push('Calls are short (<1min avg) — extending to 3 min correlates with 2x close rate');
  }
  const noAnswerCount = outcomes.filter(o => String(o).toLowerCase().includes('no answer')).length;
  if (noAnswerCount >= 3 && noAnswerCount >= callHistory.length * 0.6) {
    coachingSignals.push('60%+ of calls unanswered — try a different time-of-day window');
  }

  return (
    <div style={{
      background: '#fefce8',
      border: '1px solid #fde047',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#a16207', fontWeight: 800 }}>
          Call Intelligence
        </div>
        <div style={{ fontSize: 11, color: '#78716c' }}>
          {callHistory.length} call{callHistory.length === 1 ? '' : 's'} analyzed
        </div>
      </div>

      {/* Aggregates row */}
      <div style={{
        display: 'grid', gap: 6,
        gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        marginBottom: 10,
      }}>
        {avgScore != null && (
          <div style={{ background: '#ffffff', border: '1px solid #fef08a', borderRadius: 6, padding: '6px 8px' }}>
            <div style={{ fontSize: 9, color: '#a16207', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>Avg score</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: avgScore >= 70 ? '#059669' : avgScore >= 50 ? '#d97706' : '#dc2626', fontFamily: 'JetBrains Mono, monospace' }}>{avgScore}</div>
          </div>
        )}
        <div style={{ background: '#ffffff', border: '1px solid #fef08a', borderRadius: 6, padding: '6px 8px' }}>
          <div style={{ fontSize: 9, color: '#a16207', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>Total talk</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', fontFamily: 'JetBrains Mono, monospace' }}>{fmtDuration(totalDuration)}</div>
        </div>
        {longestCall?.duration > 0 && (
          <div style={{ background: '#ffffff', border: '1px solid #fef08a', borderRadius: 6, padding: '6px 8px' }}>
            <div style={{ fontSize: 9, color: '#a16207', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>Longest</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', fontFamily: 'JetBrains Mono, monospace' }}>{fmtDuration(longestCall.duration)}</div>
          </div>
        )}
        {topOutcome && (
          <div style={{ background: '#ffffff', border: '1px solid #fef08a', borderRadius: 6, padding: '6px 8px' }}>
            <div style={{ fontSize: 9, color: '#a16207', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>Most common</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>{topOutcome[0]} ({topOutcome[1]})</div>
          </div>
        )}
      </div>

      {/* Recent calls list */}
      <div style={{ display: 'grid', gap: 4 }}>
        {recentCalls.map((c, i) => {
          const s = outcomeStyle(c.outcome);
          const score = Number(c.aiScore || c.ai_score || 0);
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#ffffff', border: '1px solid #fef08a',
              borderRadius: 6, padding: '6px 10px',
            }}>
              <div style={{ fontSize: 10, color: '#78716c', fontFamily: 'JetBrains Mono, monospace', minWidth: 65 }}>
                {c.at ? new Date(c.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
              </div>
              <div style={{ fontSize: 10, color: '#78716c', minWidth: 55 }}>
                {fmtDuration(Number(c.duration || c.duration_sec || 0))}
              </div>
              <div style={{
                background: s.bg, color: s.text, border: `1px solid ${s.border}`,
                padding: '1px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: 'capitalize',
              }}>{c.outcome || 'logged'}</div>
              {score > 0 && (
                <div style={{
                  marginLeft: 'auto', fontSize: 10, color: '#a16207',
                  fontFamily: 'JetBrains Mono, monospace', fontWeight: 800,
                }}>{score}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Coaching hints */}
      {coachingSignals.length > 0 && (
        <div style={{ marginTop: 10, padding: '8px 10px', background: '#ffffff', border: '1px solid #fef08a', borderRadius: 6 }}>
          <div style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#a16207', fontWeight: 800, marginBottom: 4 }}>
            AI Coaching
          </div>
          {coachingSignals.map((s, i) => (
            <div key={i} style={{ fontSize: 11, color: '#78716c', marginTop: 2 }}>
              · {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
